import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  SlidersHorizontal, BarChart3, LineChart as LineIcon, AreaChart as AreaIcon,
  PieChart as PieIcon, ScatterChart, Table2, LayoutGrid, GitCompareArrows, Map as MapIcon,
} from 'lucide-react';

// Chart types offered in the quick-switch (data-shape agnostic subset).
const TYPES = [
  { id: 'bar_chart', label: 'Bar', Icon: BarChart3 },
  { id: 'line_chart', label: 'Line', Icon: LineIcon },
  { id: 'area_chart', label: 'Area', Icon: AreaIcon },
  { id: 'pie_chart', label: 'Pie', Icon: PieIcon },
  { id: 'scatter', label: 'Scatter', Icon: ScatterChart },
  { id: 'treemap', label: 'Treemap', Icon: LayoutGrid },
  { id: 'combo', label: 'Combo', Icon: GitCompareArrows },
  { id: 'geo_map', label: 'Map', Icon: MapIcon },
  { id: 'table', label: 'Table', Icon: Table2 },
];
const PALETTES = ['default', 'warm', 'cool', 'earth', 'monochrome'];
const SWATCH = {
  default: '#3b82f6', warm: '#f97316', cool: '#06b6d4', earth: '#a16207', monochrome: '#94a3b8',
};
const SORTS = [
  { id: 'none', label: 'Default' },
  { id: 'measure_desc', label: 'Value ↓' },
  { id: 'measure_asc', label: 'Value ↑' },
  { id: 'dimension_asc', label: 'A–Z' },
];
const TOPN = [
  { id: 0, label: 'All' }, { id: 5, label: '5' }, { id: 10, label: '10' }, { id: 20, label: '20' },
];

const Section = ({ title, children }) => (
  <div className="px-3 py-2 border-b border-border/20 last:border-0">
    <p className="text-[9px] uppercase tracking-wider text-muted-foreground/70 font-semibold mb-1.5">{title}</p>
    {children}
  </div>
);

const Chip = ({ active, onClick, testid, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-2 py-1 rounded-md text-[10px] transition-colors ${active ? 'bg-primary/20 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/40'}`}
  >
    {children}
  </button>
);

// Per-result view controls. All changes are applied to the message's viz_state
// (client-side + persisted) — NO AI request.
export function VizControls({ result, vizState, onChange, isChart, hasMultiSeries, measures = [], effectiveUnits = null, onUnitOverride }) {
  const FORMAT_OPTS = [
    { value: 'none', label: 'Number' },
    { value: 'USD', label: 'Currency ($)' },
    { value: 'USD_thousands', label: '$ thousands' },
    { value: 'USD_millions', label: '$ millions' },
    { value: 'percent', label: 'Percent (0–100)' },
    { value: 'percent_decimal', label: 'Percent (ratio)' },
    { value: 'ratio', label: 'Ratio (×)' },
    { value: 'count', label: 'Count' },
  ];
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const [coords, setCoords] = useState(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  // Position the panel as a fixed, body-level layer anchored to the button so
  // it is never clipped by the chart panel's overflow. Right-aligned to the
  // button; flips up if there isn't room below.
  useEffect(() => {
    if (!open || !btnRef.current) return;
    const update = () => {
      const r = btnRef.current.getBoundingClientRect();
      const PANEL_W = 224, PANEL_MAX = 460;
      const left = Math.max(8, Math.min(r.right - PANEL_W, window.innerWidth - PANEL_W - 8));
      const spaceBelow = window.innerHeight - r.bottom - 12;
      const spaceAbove = r.top - 12;
      let top, maxHeight;
      if (spaceBelow >= 240 || spaceBelow >= spaceAbove) {
        maxHeight = Math.max(160, Math.min(PANEL_MAX, spaceBelow));
        top = r.bottom + 6;
      } else {
        maxHeight = Math.max(160, Math.min(PANEL_MAX, spaceAbove));
        top = Math.max(8, r.top - 6 - maxHeight);
      }
      setCoords({ top, left, maxHeight });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => { window.removeEventListener('resize', update); window.removeEventListener('scroll', update, true); };
  }, [open]);

  const vs = vizState || {};
  const currentType = vs.outputType === 'table' ? 'table' : (vs.chartType || result?.chart_type || 'bar_chart');
  const legendOn = vs.showLegend !== false;
  const gridOn = vs.showGrid !== false;
  const labelsOn = vs.dataLabels ?? (() => { try { return sessionStorage.getItem('exploreDataLabels') === '1'; } catch { return false; } })();
  const sortId = vs.sort ? (vs.sort.by === 'dimension' ? 'dimension_asc' : `measure_${vs.sort.dir || 'desc'}`) : 'none';
  const topN = vs.topN || 0;
  const isGeo = currentType === 'geo_map' || result?.chart_type === 'geo_map';
  const geoMode = vs.geoMode || 'choropleth';
  const basemap = vs.basemap || 'streets';
  const geoScope = vs.geoScope || 'auto';

  const pickType = (id) => onChange(id === 'table'
    ? { outputType: 'table' }
    : { outputType: 'chart', chartType: id });
  const pickSort = (id) => onChange({
    sort: id === 'none' ? null
      : id === 'dimension_asc' ? { by: 'dimension', dir: 'asc' }
      : { by: 'measure', dir: id === 'measure_asc' ? 'asc' : 'desc' },
  });

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        className={`p-1.5 rounded transition-colors ${open ? 'bg-primary/20 text-primary' : 'hover:bg-muted/40 text-muted-foreground hover:text-foreground'}`}
        title="Customize view (no AI)"
        data-testid="viz-controls-btn"
      >
        <SlidersHorizontal className="w-3.5 h-3.5" />
      </button>
      {open && coords && createPortal(
        <div
          ref={ref}
          style={{ position: 'fixed', top: coords.top, left: coords.left, maxHeight: coords.maxHeight, width: 224 }}
          className="z-[9999] rounded-xl bg-popover border border-border/40 shadow-xl overflow-y-auto overflow-x-hidden overscroll-contain"
          data-testid="viz-controls-panel"
        >
          <Section title="Chart type">
            <div className="grid grid-cols-4 gap-1">
              {TYPES.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => pickType(id)}
                  data-testid={`viz-type-${id}`}
                  title={label}
                  className={`flex flex-col items-center gap-0.5 py-1.5 rounded-md transition-colors ${currentType === id ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted/40'}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="text-[8px]">{label}</span>
                </button>
              ))}
            </div>
          </Section>

          {isGeo && (
            <Section title="Map presentation">
              <div className="flex flex-wrap gap-1">
                {[['choropleth', 'Choropleth'], ['points', 'Points'], ['heatmap', 'Heat map']].map(([id, label]) => (
                  <Chip key={id} active={geoMode === id} onClick={() => onChange({ geoMode: id })} testid={`viz-geomode-${id}`}>{label}</Chip>
                ))}
              </div>
              {(geoMode === 'points' || geoMode === 'heatmap') && (
                <div className="mt-2">
                  <p className="text-[8px] uppercase tracking-wider text-muted-foreground/80 mb-1">Basemap</p>
                  <div className="flex flex-wrap gap-1">
                    {[['streets', 'Streets'], ['satellite', 'Satellite'], ['terrain', 'Terrain']].map(([id, label]) => (
                      <Chip key={id} active={(basemap === id) || (!vs.basemap && id === 'streets')} onClick={() => onChange({ basemap: id })} testid={`viz-basemap-${id}`}>{label}</Chip>
                    ))}
                  </div>
                </div>
              )}
              {geoMode === 'choropleth' && (
                <div className="mt-2">
                  <p className="text-[8px] uppercase tracking-wider text-muted-foreground/80 mb-1">Basemap</p>
                  <div className="flex flex-wrap gap-1">
                    <Chip active={!vs.basemap || vs.basemap === 'vector'} onClick={() => onChange({ basemap: 'vector' })} testid="viz-basemap-vector">Vector</Chip>
                    {[['streets', 'Streets'], ['satellite', 'Satellite'], ['terrain', 'Terrain']].map(([id, label]) => (
                      <Chip key={id} active={vs.basemap === id} onClick={() => onChange({ basemap: id })} testid={`viz-basemap-${id}`}>{label}</Chip>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-2">
                <p className="text-[8px] uppercase tracking-wider text-muted-foreground/80 mb-1">Region</p>
                <div className="flex flex-wrap gap-1">
                  {[['auto', 'Auto'], ['us-states', 'US States'], ['world', 'World']].map(([id, label]) => (
                    <Chip key={id} active={geoScope === id} onClick={() => onChange({ geoScope: id })} testid={`viz-geoscope-${id}`}>{label}</Chip>
                  ))}
                </div>
              </div>
            </Section>
          )}

          {isChart && (
            <Section title="Display">
              <div className="flex flex-wrap gap-1">
                <Chip active={legendOn} onClick={() => onChange({ showLegend: !legendOn })} testid="viz-legend-toggle">Legend</Chip>
                <Chip active={gridOn} onClick={() => onChange({ showGrid: !gridOn })} testid="viz-grid-toggle">Grid</Chip>
                <Chip active={labelsOn} onClick={() => onChange({ dataLabels: !labelsOn })} testid="viz-labels-toggle">Labels</Chip>
                {currentType === 'bar_chart' && (
                  <Chip active={vs.orientation === 'horizontal'} onClick={() => onChange({ orientation: vs.orientation === 'horizontal' ? 'vertical' : 'horizontal' })} testid="viz-orientation-toggle">Horizontal</Chip>
                )}
              </div>
              {hasMultiSeries && (currentType === 'bar_chart' || currentType === 'area_chart') && (
                <div className="flex gap-1 mt-1.5">
                  <Chip active={!vs.barLayout || vs.barLayout === 'grouped'} onClick={() => onChange({ barLayout: 'grouped', stacked: false })} testid="viz-stack-grouped">Grouped</Chip>
                  <Chip active={vs.barLayout === 'stacked'} onClick={() => onChange({ barLayout: 'stacked', stacked: true })} testid="viz-stack-stacked">Stacked</Chip>
                  <Chip active={vs.barLayout === 'percent'} onClick={() => onChange({ barLayout: 'percent', stacked: true })} testid="viz-stack-percent">100%</Chip>
                </div>
              )}
            </Section>
          )}

          {onUnitOverride && (measures || []).length > 0 && (
            <Section title="Number format">
              <div className="space-y-1.5">
                {(measures || []).map(m => (
                  <div key={m} className="flex items-center justify-between gap-2">
                    <span className="text-[10px] text-foreground/80 truncate flex-1" title={m}>{m}</span>
                    <select
                      value={(effectiveUnits && effectiveUnits[m]) || 'none'}
                      onChange={e => onUnitOverride(m, e.target.value)}
                      data-testid={`viz-format-${m}`}
                      className="text-[10px] rounded-md bg-muted/40 border border-border/40 px-1.5 py-0.5 text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
                    >
                      {FORMAT_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <p className="text-[9px] text-muted-foreground/70 mt-1.5">Applies to this answer's chart and table.</p>
            </Section>
          )}

          {isChart && (
            <Section title="Colours">
              <div className="flex items-center gap-1.5">
                {PALETTES.map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => onChange({ palette: p, colors: null })}
                    data-testid={`viz-palette-${p}`}
                    title={p}
                    className={`w-5 h-5 rounded-full border-2 transition-transform ${(vs.palette || 'default') === p && !vs.colors ? 'border-primary scale-110' : 'border-transparent'}`}
                    style={{ background: SWATCH[p] }}
                  />
                ))}
              </div>
            </Section>
          )}

          <Section title="Sort">
            <div className="flex flex-wrap gap-1">
              {SORTS.map(s => (
                <Chip key={s.id} active={sortId === s.id} onClick={() => pickSort(s.id)} testid={`viz-sort-${s.id}`}>{s.label}</Chip>
              ))}
            </div>
          </Section>

          <Section title="Show top">
            <div className="flex gap-1">
              {TOPN.map(n => (
                <Chip key={n.id} active={topN === n.id} onClick={() => onChange({ topN: n.id || null })} testid={`viz-topn-${n.id}`}>{n.label}</Chip>
              ))}
            </div>
          </Section>

          {isChart && (
            <Section title="Reference line">
              <div className="flex flex-wrap gap-1 items-center">
                <Chip active={!vs.refLine || vs.refLine.mode === 'none'} onClick={() => onChange({ refLine: { mode: 'none' } })} testid="viz-refline-none">None</Chip>
                <Chip active={vs.refLine?.mode === 'avg'} onClick={() => onChange({ refLine: { mode: 'avg' } })} testid="viz-refline-avg">Average</Chip>
                <Chip active={vs.refLine?.mode === 'target'} onClick={() => onChange({ refLine: { mode: 'target', value: vs.refLine?.value ?? 0 } })} testid="viz-refline-target">Target</Chip>
                {vs.refLine?.mode === 'target' && (
                  <input
                    type="number"
                    defaultValue={vs.refLine?.value ?? 0}
                    onBlur={(e) => onChange({ refLine: { mode: 'target', value: parseFloat(e.target.value) || 0 } })}
                    className="w-20 h-6 text-[10px] px-1.5 rounded-md bg-muted/50 border border-border/40 text-foreground"
                    data-testid="viz-refline-value"
                  />
                )}
              </div>
            </Section>
          )}

          {isChart && measures.length >= 2 && (
            <Section title="Second axis (right)">
              <div className="mb-1.5">
                <Chip
                  active={currentType === 'bar_chart' && vs.secondAxis?.field === measures[1] && vs.secondAxis?.chartType === 'line'}
                  onClick={() => onChange({ outputType: 'chart', chartType: 'bar_chart', secondAxis: { field: measures[1], chartType: 'line' } })}
                  testid="viz-combo-preset"
                >
                  ⚡ Bars + Line preset
                </Chip>
              </div>
              <div className="flex flex-wrap gap-1">
                <Chip active={!vs.secondAxis} onClick={() => onChange({ secondAxis: null })} testid="viz-secondaxis-none">None</Chip>
                {measures.map(m => (
                  <Chip
                    key={m}
                    active={vs.secondAxis?.field === m}
                    onClick={() => onChange({ secondAxis: { field: m, chartType: vs.secondAxis?.chartType || 'line' } })}
                    testid={`viz-secondaxis-${m}`}
                  >
                    {m}
                  </Chip>
                ))}
              </div>
              {vs.secondAxis && (
                <div className="flex gap-1 mt-1.5">
                  {['line', 'bar', 'area'].map(ct => (
                    <Chip
                      key={ct}
                      active={(vs.secondAxis.chartType || 'line') === ct}
                      onClick={() => onChange({ secondAxis: { ...vs.secondAxis, chartType: ct } })}
                      testid={`viz-secondaxis-type-${ct}`}
                    >
                      {ct}
                    </Chip>
                  ))}
                </div>
              )}
            </Section>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
